import { shiftParticipantEntityName } from '../../../../backend/features/shift/shared';
import { ShiftParticipantTableActions } from '../../../../ui/features/ongoingShifts/components/ShiftParticipantTableActions';
import { useCheckInCheckoutActions } from '../../../../ui/features/ongoingShifts/hooks/useCheckInCheckoutActions';
import { useMissionsTranslation } from '../../../../ui/hooks';
import { DataTable, defaultRowsPerPageOptions, TextCell, useDataTableUsingState } from '@juvare/common-data-table-ui/DataTable';
import { useFilterConfig } from '@juvare/common-data-table-ui/FiltersBar';
import { showConfirmDialog } from '@juvare/common-web-ui/DialogHelper';
import { themeColors } from '@juvare/common-web-ui/JuvareTheme';
import { useEntityDataTable } from '@juvare/platform-sdk-react/ui/hooks';
import { Box, Paper, Typography } from '@mui/material';
import { intervalToDuration } from 'date-fns';
import React from 'react';
export const ShiftParticipantTable = ({ shift, onCheckInCheckOutSuccess }) => {
    const { t } = useMissionsTranslation();
    const dateNow = React.useMemo(() => Date.now(), []);
    const filterConfig = useFilterConfig((builder) => {
        builder.quickSearchFilter({
            dataPath: 'search',
            label: t('common.search'),
            sx: { maxWidth: { xs: undefined, sm: 340 } },
        });
        builder.quickFilterSelect({
            dataPath: 'shiftAttendanceStatus',
            widget: 'form.select',
            label: t('shifts.ongoingShifts.timeStatus'),
            values: [
                {
                    label: t('common.checkedIn'),
                    value: 'checkedIn',
                },
                {
                    label: t('common.checkedOut'),
                    value: 'checkedOut',
                },
            ],
            sx: { maxWidth: { xs: undefined, sm: 340 } },
        });
    }, [t]);
    const mapEntityFilterData = (filterData) => {
        const filter = [
            {
                column: 'shiftId',
                op: '=',
                value: shift.id,
            },
            {
                column: 'status',
                op: '=',
                value: 'approved',
            },
        ];
        if (filterData.search) {
            filter.push({ column: 'participantFullName', op: 'contains', value: filterData.search });
        }
        if (filterData.shiftAttendanceStatus) {
            filter.push({ column: 'shiftAttendanceStatus', op: '=', value: filterData.shiftAttendanceStatus });
        }
        return filter;
    };
    const columns = React.useMemo(() => [
        {
            headerLabel: t('common.name'),
            sortKey: 'participantFullName',
            cellFunction: ({ columns }) => React.createElement(TextCell, null, columns.participantFullName),
        },
        {
            headerLabel: t('common.position'),
            cellFunction: ({ columns }) => React.createElement(TextCell, null, columns.positionName),
        },
        {
            headerLabel: t('shifts.ongoingShifts.checkInTime'),
            cellFunction: ({ columns }) => {
                var _a;
                if (!columns.hoursFrom) {
                    return React.createElement(TextCell, null, "-");
                }
                const start = columns.hoursFrom;
                const end = (_a = columns.hoursTo) !== null && _a !== void 0 ? _a : dateNow;
                const { hours = 0, minutes = 0 } = intervalToDuration({ start, end });
                const duration = `${String(hours).padStart(2, '0')}h:${String(minutes).padStart(2, '0')}m`;
                return React.createElement(TextCell, null, `${t('time', { value: columns.hoursFrom })}, (${duration})`);
            },
        },
        {
            cellFunction: (row) => React.createElement(ActionsColumn, { row: row }),
        },
    ], [dateNow, t]);
    const { dataTableProps, reload } = useEntityDataTable({
        entityName: shiftParticipantEntityName,
        title: t('shifts.ongoingShifts.shiftRoster'),
        columns,
        defaultSortColumnKey: 'participantFullName',
        filterConfig,
        mapEntityFilterData,
    }, { defaultRowsPerPage: 5, useDataTableFunction: useDataTableUsingState, onRowClick: () => undefined });
    const ActionsColumn = ({ row }) => {
        const { handleCheckInVolunteer, handleCheckOutVolunteer } = useCheckInCheckoutActions();
        const handleCheckInParticipant = () => {
            showConfirmDialog({
                title: t('shifts.ongoingShifts.dialogs.checkInConfirmation.title', { name: row.columns.participantFullName }),
                content: (React.createElement(Typography, { variant: 'body1', color: themeColors.text.subtle }, t('shifts.ongoingShifts.dialogs.checkInConfirmation.content'))),
                confirmLabel: t('shifts.ongoingShifts.actionLabel.checkIn'),
                onConfirm: () => {
                    handleCheckInVolunteer({ participationId: row.id }).then(() => {
                        reload();
                        onCheckInCheckOutSuccess === null || onCheckInCheckOutSuccess === void 0 ? void 0 : onCheckInCheckOutSuccess();
                    });
                },
            });
        };
        const handleCheckOutParticipant = () => {
            showConfirmDialog({
                title: t('shifts.ongoingShifts.dialogs.checkOutConfirmation.title', { name: row.columns.participantFullName }),
                content: (React.createElement(Typography, { variant: 'body1', color: themeColors.text.subtle }, t('shifts.ongoingShifts.dialogs.checkOutConfirmation.content'))),
                confirmLabel: t('shifts.ongoingShifts.actionLabel.checkOut'),
                onConfirm: () => {
                    handleCheckOutVolunteer({ participationId: row.id }).then(() => {
                        reload();
                        onCheckInCheckOutSuccess === null || onCheckInCheckOutSuccess === void 0 ? void 0 : onCheckInCheckOutSuccess();
                    });
                },
            });
        };
        return (React.createElement(Box, { sx: { display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '8px', flexGrow: 1 } },
            React.createElement(ShiftParticipantTableActions, { shiftAttendanceStatus: row.columns.shiftAttendanceStatus, onCheckinParticipant: handleCheckInParticipant, onCheckoutParticipant: handleCheckOutParticipant })));
    };
    return (React.createElement(DataTable, Object.assign({}, dataTableProps, { rowsPerPageOptions: [5, ...defaultRowsPerPageOptions], customTableSurfaceComponent: CustomTableSurface })));
};
const CustomTableSurface = ({ children }) => (React.createElement(Paper, { sx: { border: (theme) => '1px solid ' + theme.palette.divider, boxShadow: 'none' } }, children));
